import { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import API from "../services/api";
import Loader from "../components/Loader";
import { FaUserPlus, FaSearch, FaUserShield, FaBuilding, FaEnvelope, FaEye, FaEdit, FaToggleOn, FaToggleOff, FaUserCheck, FaUserTimes } from "react-icons/fa";

function Users() {
    const [users, setUsers] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [search, setSearch] = useState("");
    
    // Modals state
    const [showAddModal, setShowAddModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    
    const [selectedUser, setSelectedUser] = useState(null);
    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);

    // Current logged-in user role
    const currentUserRole = localStorage.getItem("role");

    // Add Form State
    const [addForm, setAddForm] = useState({
        name: "",
        email: "",
        password: "",
        role: "viewer",
        department: ""
    });

    // Edit Form State
    const [editForm, setEditForm] = useState({
        name: "",
        email: "",
        password: "", // to reset password
        role: "viewer",
        department: "",
        isActive: true
    });

    // =====================================
    // FETCH DATA
    // =====================================
    const fetchData = async () => {
        try {
            setPageLoading(true);
            const userRes = await API.get("/auth/users");
            setUsers(userRes.data.users);

            const depRes = await API.get("/department/list");
            setDepartments(depRes.data.departments);
        } catch (error) {
            console.error("Failed to fetch data", error);
        } finally {
            setPageLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // =====================================
    // ADD NEW USER
    // =====================================
    const handleAddUser = async (e) => {
        e.preventDefault();
        if (!addForm.name.trim() || !addForm.email.trim() || !addForm.password.trim() || !addForm.department) {
            alert("Please fill in all required fields.");
            return;
        }

        setLoading(true);
        try {
            const response = await API.post("/auth/add-user", addForm);
            alert(response.data.message || "User Created successfully!");
            setShowAddModal(false);
            setAddForm({
                name: "",
                email: "",
                password: "",
                role: "viewer",
                department: ""
            });
            fetchData();
        } catch (error) {
            alert(error.response?.data?.message || "Failed to create user.");
        } finally {
            setLoading(false);
        }
    };

    // =====================================
    // EDIT USER DETAILS
    // =====================================
    const handleEditUser = async (e) => {
        e.preventDefault();
        if (!editForm.name.trim() || !editForm.email.trim() || !editForm.department) {
            alert("Please fill in all required fields.");
            return;
        }

        setLoading(true);
        try {
            const response = await API.put(`/auth/update-user/${selectedUser._id}`, editForm);
            alert(response.data.message || "User account updated!");
            setShowEditModal(false);
            fetchData();
        } catch (error) {
            alert(error.response?.data?.message || "Failed to update user.");
        } finally {
            setLoading(false);
        }
    };

    // =====================================
    // TOGGLE STATUS QUICK ACTION (ADMIN ONLY)
    // =====================================
    const handleToggleStatus = async (user) => {
        const actionText = user.isActive ? "deactivate" : "activate";
        const confirmToggle = window.confirm(`Are you sure you want to ${actionText} the account for ${user.name}?`);
        
        if (!confirmToggle) return;

        try {
            await API.put(`/auth/update-user/${user._id}`, {
                isActive: !user.isActive
            });
            alert(`User account ${user.isActive ? "deactivated" : "activated"} successfully.`);
            fetchData();
        } catch (error) {
            alert(error.response?.data?.message || "Failed to change user status.");
        }
    };

    // =====================================
    // OPEN EDIT MODAL
    // =====================================
    const openEditModal = (user) => {
        setSelectedUser(user);
        
        // Find matching department name or ID
        const matchedDep = departments.find(d => d.name === user.department || d._id === user.department);

        setEditForm({
            name: user.name,
            email: user.email,
            password: "", // blank by default
            role: user.role,
            department: matchedDep ? matchedDep.name : user.department || "",
            isActive: user.isActive !== false
        });
        setShowEditModal(true);
    };

    // =====================================
    // OPEN VIEW MODAL
    // =====================================
    const openViewModal = (user) => {
        setSelectedUser(user);
        setShowViewModal(true);
    };

    // =====================================
    // CLIENT SIDE FILTERING
    // =====================================
    const filteredUsers = users.filter((u) => 
        u.name.toLowerCase().includes(search.toLowerCase()) ||
        u.email.toLowerCase().includes(search.toLowerCase()) ||
        u.role.toLowerCase().includes(search.toLowerCase()) ||
        (u.department && u.department.toLowerCase().includes(search.toLowerCase()))
    );

    return (
        <div className="main-wrapper">
            <Sidebar />

            <div className="content-wrapper">
                {/* HEADER */}
                <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-3 mb-4 animate-fade-in">
                    <div>
                        <h2 className="fw-extrabold text-slate mb-1">User Management</h2>
                        <p className="text-muted mb-0">Create and manage accounts for portal managers and viewers</p>
                    </div>

                    <button
                        className="btn btn-primary d-flex align-items-center gap-2 shadow-sm"
                        onClick={() => {
                            setAddForm({
                                name: "",
                                email: "",
                                password: "",
                                role: "viewer",
                                department: ""
                            });
                            setShowAddModal(true);
                        }}
                    >
                        <FaUserPlus size={14} /> Add New User
                    </button>
                </div>

                {/* SEARCH CARD */}
                <div className="card border-0 shadow-sm mb-4 animate-fade-in" style={{ animationDelay: "0.1s" }}>
                    <div className="card-body p-3">
                        <div className="input-group">
                            <span className="input-group-text bg-transparent border-0 pe-0" style={{ color: "#94a3b8" }}>
                                <FaSearch />
                            </span>
                            <input
                                type="text"
                                placeholder="Search users by name, email, role, or department..."
                                className="form-control border-0 shadow-none bg-transparent"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                {pageLoading ? (
                    <Loader message="Loading system users..." />
                ) : (
                    <>
                        {/* USERS TABLE */}
                        <div className="card border-0 shadow-sm animate-fade-in" style={{ animationDelay: "0.2s" }}>
                            <div className="card-body p-0">
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th className="ps-4" style={{ width: "60px" }}>#</th>
                                        <th>Name</th>
                                        <th>Email</th>
                                        <th>Role</th>
                                        <th>Department</th>
                                        <th>Status</th>
                                        <th className="pe-4 text-end" style={{ width: "160px" }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredUsers.length > 0 ? (
                                        filteredUsers.map((user, index) => (
                                            <tr key={user._id} className="hover-bg-light">
                                                <td className="ps-4 text-muted font-monospace">{index + 1}</td>
                                                <td>
                                                    <span className="fw-bold text-dark">{user.name}</span>
                                                </td>
                                                <td>
                                                    <span className="text-slate d-flex align-items-center gap-1">
                                                        <FaEnvelope size={12} className="text-muted" /> {user.email}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span 
                                                        className="badge px-3 py-2 text-uppercase font-monospace"
                                                        style={{
                                                            background: user.role === "admin" 
                                                                ? "rgba(239, 68, 68, 0.1)" 
                                                                : user.role === "manager" 
                                                                    ? "rgba(245, 158, 11, 0.1)" 
                                                                    : "rgba(79, 70, 229, 0.1)",
                                                            color: user.role === "admin" 
                                                                ? "#ef4444" 
                                                                : user.role === "manager" 
                                                                    ? "#f59e0b" 
                                                                    : "#4f46e5",
                                                            fontWeight: "700"
                                                        }}
                                                    >
                                                        {user.role}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="text-muted">
                                                        {user.department || "N/A"}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span 
                                                        className="badge px-3 py-2"
                                                        style={{
                                                            background: user.isActive !== false ? "rgba(16, 185, 129, 0.1)" : "rgba(239, 68, 68, 0.1)",
                                                            color: user.isActive !== false ? "#10b981" : "#ef4444",
                                                            fontWeight: "600"
                                                        }}
                                                    >
                                                        {user.isActive !== false ? "Active" : "Inactive"}
                                                    </span>
                                                </td>
                                                <td className="pe-4 text-end">
                                                    <div className="d-flex justify-content-end gap-2">
                                                        {/* View Details (Available for Admin & Manager) */}
                                                        <button
                                                            className="btn btn-sm d-flex align-items-center justify-content-center"
                                                            style={{
                                                                width: "32px",
                                                                height: "32px",
                                                                padding: "0",
                                                                borderRadius: "8px",
                                                                background: "rgba(14, 165, 233, 0.1)",
                                                                color: "#0ea5e9",
                                                                border: "none"
                                                            }}
                                                            onClick={() => openViewModal(user)}
                                                            title="View User Details"
                                                        >
                                                            <FaEye size={14} />
                                                        </button>

                                                        {/* Edit & Toggle Status (Admin Only) */}
                                                        {currentUserRole === "admin" && (
                                                            <>
                                                                <button
                                                                    className="btn btn-sm d-flex align-items-center justify-content-center"
                                                                    style={{
                                                                        width: "32px",
                                                                        height: "32px",
                                                                        padding: "0",
                                                                        borderRadius: "8px",
                                                                        background: "rgba(245, 158, 11, 0.1)",
                                                                        color: "#f59e0b",
                                                                        border: "none"
                                                                    }}
                                                                    onClick={() => openEditModal(user)}
                                                                    title="Edit User"
                                                                >
                                                                    <FaEdit size={14} />
                                                                </button>

                                                                <button
                                                                    className="btn btn-sm d-flex align-items-center justify-content-center"
                                                                    style={{
                                                                        width: "32px",
                                                                        height: "32px",
                                                                        padding: "0",
                                                                        borderRadius: "8px",
                                                                        background: user.isActive !== false ? "rgba(239, 68, 68, 0.1)" : "rgba(16, 185, 129, 0.1)",
                                                                        color: user.isActive !== false ? "#ef4444" : "#10b981",
                                                                        border: "none"
                                                                    }}
                                                                    onClick={() => handleToggleStatus(user)}
                                                                    title={user.isActive !== false ? "Deactivate User" : "Activate User"}
                                                                >
                                                                    {user.isActive !== false ? <FaUserTimes size={14} /> : <FaUserCheck size={14} />}
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="7" className="text-center py-5 text-muted">
                                                <h5>No Users Found</h5>
                                                <p className="small mb-0">Try adjusting your search query.</p>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                    </>
                )}

                {/* ADD USER MODAL */}
                {showAddModal && (
                    <div 
                        className="modal d-flex align-items-center justify-content-center position-fixed top-0 start-0 w-100 h-100" 
                        style={{ 
                            backgroundColor: "rgba(15, 23, 42, 0.4)", 
                            backdropFilter: "blur(4px)",
                            zIndex: 1060 
                        }}
                    >
                        <div className="modal-dialog w-100" style={{ maxWidth: "500px" }}>
                            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: "16px" }}>
                                <div className="modal-header border-bottom-0">
                                    <h5 className="fw-bold mb-0">Add New User</h5>
                                    <button
                                        className="btn-close shadow-none"
                                        onClick={() => setShowAddModal(false)}
                                    />
                                </div>
                                <form onSubmit={handleAddUser}>
                                    <div className="modal-body py-3">
                                        <div className="mb-3">
                                            <label className="form-label text-muted small fw-semibold">Full Name *</label>
                                            <input
                                                type="text"
                                                name="name"
                                                placeholder="e.g. John Doe"
                                                className="form-control"
                                                value={addForm.name}
                                                onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                                                required
                                            />
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label text-muted small fw-semibold">Email Address *</label>
                                            <input
                                                type="email"
                                                name="email"
                                                placeholder="e.g. john@example.com"
                                                className="form-control"
                                                value={addForm.email}
                                                onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
                                                required
                                            />
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label text-muted small fw-semibold">Password *</label>
                                            <input
                                                type="password"
                                                name="password"
                                                placeholder="Enter secure password"
                                                className="form-control"
                                                value={addForm.password}
                                                onChange={(e) => setAddForm({ ...addForm, password: e.target.value })}
                                                required
                                            />
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label text-muted small fw-semibold">Assign Department *</label>
                                            <div className="input-group">
                                                <span className="input-group-text bg-light border-end-0">
                                                    <FaBuilding className="text-muted" size={14} />
                                                </span>
                                                <select
                                                    name="department"
                                                    className="form-select border-start-0"
                                                    value={addForm.department}
                                                    onChange={(e) => setAddForm({ ...addForm, department: e.target.value })}
                                                    required
                                                >
                                                    <option value="">Select Department</option>
                                                    {departments.map((dep) => (
                                                        <option key={dep._id} value={dep.name}>
                                                            {dep.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                        <div className="mb-2">
                                            <label className="form-label text-muted small fw-semibold">Assign Role *</label>
                                            <div className="input-group">
                                                <span className="input-group-text bg-light border-end-0">
                                                    <FaUserShield className="text-muted" size={14} />
                                                </span>
                                                {currentUserRole === "manager" ? (
                                                    <select
                                                        name="role"
                                                        className="form-select border-start-0 bg-light"
                                                        value="viewer"
                                                        disabled
                                                    >
                                                        <option value="viewer">Viewer</option>
                                                    </select>
                                                ) : (
                                                    <select
                                                        name="role"
                                                        className="form-select border-start-0"
                                                        value={addForm.role}
                                                        onChange={(e) => setAddForm({ ...addForm, role: e.target.value })}
                                                        required
                                                    >
                                                        <option value="viewer">Viewer</option>
                                                        <option value="manager">Manager</option>
                                                    </select>
                                                )}
                                            </div>
                                            {currentUserRole === "manager" && (
                                                <div className="form-text text-amber mt-1" style={{ color: "#d97706", fontSize: "12px" }}>
                                                    ℹ️ Managers can only create Viewer accounts.
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="modal-footer border-top-0 pt-0">
                                        <button
                                            type="button"
                                            className="btn btn-light"
                                            onClick={() => setShowAddModal(false)}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary shadow-sm"
                                            disabled={loading}
                                        >
                                            {loading ? "Creating..." : "Create User"}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}

                {/* EDIT USER MODAL (ADMIN ONLY) */}
                {showEditModal && (
                    <div 
                        className="modal d-flex align-items-center justify-content-center position-fixed top-0 start-0 w-100 h-100" 
                        style={{ 
                            backgroundColor: "rgba(15, 23, 42, 0.4)", 
                            backdropFilter: "blur(4px)",
                            zIndex: 1060 
                        }}
                    >
                        <div className="modal-dialog w-100" style={{ maxWidth: "500px" }}>
                            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: "16px" }}>
                                <div className="modal-header border-bottom-0">
                                    <h5 className="fw-bold mb-0">Edit User Details</h5>
                                    <button
                                        className="btn-close shadow-none"
                                        onClick={() => setShowEditModal(false)}
                                    />
                                </div>
                                <form onSubmit={handleEditUser}>
                                    <div className="modal-body py-3">
                                        <div className="mb-3">
                                            <label className="form-label text-muted small fw-semibold">Full Name *</label>
                                            <input
                                                type="text"
                                                name="name"
                                                className="form-control"
                                                value={editForm.name}
                                                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                                required
                                            />
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label text-muted small fw-semibold">Email Address *</label>
                                            <input
                                                type="email"
                                                name="email"
                                                className="form-control"
                                                value={editForm.email}
                                                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                                                required
                                            />
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label text-muted small fw-semibold">Reset Password (leave blank to keep current)</label>
                                            <input
                                                type="password"
                                                name="password"
                                                placeholder="Enter new password to reset"
                                                className="form-control"
                                                value={editForm.password}
                                                onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                                            />
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label text-muted small fw-semibold">Assign Department *</label>
                                            <div className="input-group">
                                                <span className="input-group-text bg-light border-end-0">
                                                    <FaBuilding className="text-muted" size={14} />
                                                </span>
                                                <select
                                                    name="department"
                                                    className="form-select border-start-0"
                                                    value={editForm.department}
                                                    onChange={(e) => setEditForm({ ...editForm, department: e.target.value })}
                                                    required
                                                >
                                                    <option value="">Select Department</option>
                                                    {departments.map((dep) => (
                                                        <option key={dep._id} value={dep.name}>
                                                            {dep.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label text-muted small fw-semibold">Assign Role *</label>
                                            <div className="input-group">
                                                <span className="input-group-text bg-light border-end-0">
                                                    <FaUserShield className="text-muted" size={14} />
                                                </span>
                                                <select
                                                    name="role"
                                                    className="form-select border-start-0"
                                                    value={editForm.role}
                                                    onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                                                    required
                                                >
                                                    <option value="viewer">Viewer</option>
                                                    <option value="manager">Manager</option>
                                                    <option value="admin">Admin</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div className="mb-2">
                                            <label className="form-label text-muted small fw-semibold">Account Status</label>
                                            <div className="input-group">
                                                <span className="input-group-text bg-light border-end-0">
                                                    {editForm.isActive ? <FaToggleOn className="text-success" size={16} /> : <FaToggleOff className="text-danger" size={16} />}
                                                </span>
                                                <select
                                                    name="isActive"
                                                    className="form-select border-start-0"
                                                    value={editForm.isActive ? "true" : "false"}
                                                    onChange={(e) => setEditForm({ ...editForm, isActive: e.target.value === "true" })}
                                                    required
                                                >
                                                    <option value="true">Active</option>
                                                    <option value="false">Inactive / Deactivated</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="modal-footer border-top-0 pt-0">
                                        <button
                                            type="button"
                                            className="btn btn-light"
                                            onClick={() => setShowEditModal(false)}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary shadow-sm"
                                            disabled={loading}
                                        >
                                            {loading ? "Saving..." : "Save Changes"}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}

                {/* VIEW USER DETAILS MODAL */}
                {showViewModal && (
                    <div 
                        className="modal d-flex align-items-center justify-content-center position-fixed top-0 start-0 w-100 h-100" 
                        style={{ 
                            backgroundColor: "rgba(15, 23, 42, 0.4)", 
                            backdropFilter: "blur(4px)",
                            zIndex: 1060 
                        }}
                    >
                        <div className="modal-dialog w-100" style={{ maxWidth: "500px" }}>
                            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: "16px" }}>
                                <div className="modal-header border-bottom-0 pb-0">
                                    <h5 className="fw-bold mb-0">User Account Profile</h5>
                                    <button
                                        className="btn-close shadow-none"
                                        onClick={() => setShowViewModal(false)}
                                    />
                                </div>
                                <div className="modal-body py-4">
                                    <div className="mb-3">
                                        <label className="text-muted small fw-semibold text-uppercase">Full Name</label>
                                        <div className="fs-5 fw-bold text-dark mt-1">{selectedUser?.name}</div>
                                    </div>
                                    <div className="mb-3">
                                        <label className="text-muted small fw-semibold text-uppercase">Email Address</label>
                                        <div className="text-slate mt-1">{selectedUser?.email}</div>
                                    </div>
                                    <div className="mb-3">
                                        <label className="text-muted small fw-semibold text-uppercase">Portal Access Role</label>
                                        <div className="mt-1">
                                            <span 
                                                className="badge px-3 py-2 text-uppercase font-monospace"
                                                style={{
                                                    background: selectedUser?.role === "admin" 
                                                        ? "rgba(239, 68, 68, 0.1)" 
                                                        : selectedUser?.role === "manager" 
                                                            ? "rgba(245, 158, 11, 0.1)" 
                                                            : "rgba(79, 70, 229, 0.1)",
                                                    color: selectedUser?.role === "admin" 
                                                        ? "#ef4444" 
                                                        : selectedUser?.role === "manager" 
                                                            ? "#f59e0b" 
                                                            : "#4f46e5",
                                                    fontWeight: "700"
                                                }}
                                            >
                                                {selectedUser?.role}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <label className="text-muted small fw-semibold text-uppercase">Department</label>
                                        <div className="text-slate mt-1">{selectedUser?.department || "N/A"}</div>
                                    </div>
                                    <div className="mb-3">
                                        <label className="text-muted small fw-semibold text-uppercase">Account Status</label>
                                        <div className="mt-1">
                                            <span 
                                                className="badge px-3 py-2"
                                                style={{
                                                    background: selectedUser?.isActive !== false ? "rgba(16, 185, 129, 0.1)" : "rgba(239, 68, 68, 0.1)",
                                                    color: selectedUser?.isActive !== false ? "#10b981" : "#ef4444",
                                                    fontWeight: "600"
                                                }}
                                            >
                                                {selectedUser?.isActive !== false ? "Active" : "Inactive"}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="row g-2 pt-2 border-top border-light">
                                        <div className="col-6">
                                            <label className="text-muted small">Created At</label>
                                            <div className="small font-monospace mt-1">
                                                {selectedUser?.createdAt ? new Date(selectedUser.createdAt).toLocaleString() : "N/A"}
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <label className="text-muted small">Last Updated</label>
                                            <div className="small font-monospace mt-1">
                                                {selectedUser?.updatedAt ? new Date(selectedUser.updatedAt).toLocaleString() : "N/A"}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Users;
